import Cookies from "js-cookie";
import _ from "underscore";
import API from "../api/api/API";
import TenantAPI from "../api/api/TenantAPI";

export const getCurrentUser = async () => {
  const data = await API.getUser();

  const currentUser = data?.data?.data?.user;

  return currentUser ?? null;
};

export const fetchAllPermissions = async (setCurrentCloudData) => {
  const newCloudPermissions = [];
  const newWorkspacePermissions = [];
  const newPagePermissions = [];

  const data = await TenantAPI.getPermissionProfiles();
  const permissionProfiles = data.data.data.profiles;
  const cloud = data.data.data.cloud;

  setCurrentCloudData(cloud);

  permissionProfiles.forEach((permissionProfile) => {
    if (permissionProfile.Type === "Cloud") {
      newCloudPermissions.push(permissionProfile);
    } else if (permissionProfile.Type === "Workspace") {
      newWorkspacePermissions.push(permissionProfile);
    } else if (permissionProfile.Type === "Page") {
      newPagePermissions.push(permissionProfile);
    }
  });

  // JSON.stringify comparisions used to live here. See old comment
  // Only update the permissions if they are new. This prevents a full tree rerender.

  return {
    cPermissions: newCloudPermissions,
    wPermissions: newWorkspacePermissions,
    pPermissions: newPagePermissions,
  };
};

export const getUserCloudPermissionID = (user, tenantID) => {
  if (!user) return null;

  const tenants = user.Tenants;
  const currentTenantID = tenantID;

  const currentTenant = _.findWhere(tenants, { TenantID: currentTenantID });

  return currentTenant?.Permissions?.CloudProfile ?? null;
};

export const getUserPermissionID = (user, key, id, tenantID = null) => {
  if (!user) return null;

  const currentTenantID = tenantID;

  const tenants = user.Tenants;
  const currentTenant = _.findWhere(tenants, { TenantID: currentTenantID });

  return currentTenant?.Permissions?.[key]?.[id] ?? null;
};

/**
 * Gets the user's permissions for a given cloud.
 * @param {*} user - The user to get the permissions for.
 * @param {*} permissions - The cloud permission profiles to search through.
 * @param {*} tenantID - The tenant ID to get the permissions for.
 * @returns {object} - The user's permissions for the given cloud.
 */
export const getUserCloudPermissions = (user, permissions, tenantID) => {
  if (!user || !permissions || !tenantID) return "";

  const permissionID = getUserCloudPermissionID(user, tenantID);

  const foundPermission = _.findWhere(permissions, { id: permissionID });

  return foundPermission;
};

/**
 * Gets the user's permissions for a given workspace.
 * @param {*} user - The user to get the permissions for.
 * @param {*} tenantID - The tenant ID to get the permissions for.
 * @param {*} workspaceID - The workspace ID to get the permissions for.
 * @param {*} permissions - The workspace permission profiles to search through.
 * @param {*} permissionProfile - Optional permission profile ID to use (In case we already have this)
 * @returns {any} - The user's permissions for the given workspace.
 */
export const getUserWorkspacePermissions = (
  user,
  tenantID,
  workspaceID,
  permissions,
  permissionProfile = null
) => {
  if (
    !permissionProfile &&
    (!user || !tenantID || !workspaceID || !permissions)
  ) {
    return null;
  }

  // If no permissionProfile provided, get the user's permission profile
  if (!permissionProfile) {
    const tenants = user.Tenants;
    const currentTenant = _.findWhere(tenants, { TenantID: tenantID });

    permissionProfile = currentTenant?.Permissions?.Workspaces?.[workspaceID];
  }

  if (!permissionProfile) return null;

  const workspacePermissions = _.findWhere(permissions, {
    id: permissionProfile,
  });

  return workspacePermissions;
};

/**
 * Gets the user's permissions for a given page.
 * @param {*} user - The user to get the permissions for.
 * @param {*} tenantID - The tenant ID to get the permissions for.
 * @param {*} workspaceID - The page ID to get the permissions for.
 * @param {*} permissions - The page permission profiles to search through.
 * @param {*} permissionProfile - Optional permission profile ID to use (In case we already have this)
 * @returns {*} The user's permissions for the given page.
 */
export const getUserPagePermissions = (
  user,
  tenantID,
  pageID,
  permissions,
  permissionProfile = null
) => {
  if (!permissionProfile && (!user || !tenantID || !pageID || !permissions)) {
    return null;
  }

  // If no permissionProfile provided, get the user's permission profile
  if (!permissionProfile) {
    const tenants = user.Tenants;
    const currentTenant = _.findWhere(tenants, { TenantID: tenantID });

    permissionProfile = currentTenant?.Permissions?.Pages?.[pageID];
  }

  if (!permissionProfile) return null;

  const pagePermissions = _.findWhere(permissions, {
    id: permissionProfile,
  });

  return pagePermissions;
};

/**
 * Checks if the user has the given permission(scope) using a permissionProfile
 * @param {*} permissionProfile - The permissionProfile to check against
 * @param {*} scope - The permission scope to check for. Example: "workspace_add_user"
 */
export const userHasScope = (permissionProfile, scope) => {
  if (!permissionProfile || !scope) return false;

  const scopes = permissionProfile.Scopes;

  if (!scopes) return false;

  return scopes.includes(scope);
};

/**
 * Updates the tenantUsers RQ cache using the queryClient. Accepts a single user, or an array of users.
 * @param {RQ QueryClient} queryClient - RQ QueryClient to use.
 * @param {string} tenantID - The current tenantID.
 * @param {Array<People> | People} updatedUser - The users to replace in the cache.
 */
export const updateTenantUsers = (queryClient, tenantID, updatedUsers) => {
  if (!queryClient || !updatedUsers) return;

  const userMap = {};

  // Create user map with the key being the user's id, for easy cache swapping
  if (Array.isArray(updatedUsers)) {
    updatedUsers.forEach((user) => {
      userMap[user.id] = user;
    });
  } else {
    userMap[updatedUsers.id] = updatedUsers;
  }

  queryClient.setQueryData(["tenantUsers", tenantID], (prev) => {
    if (!Array.isArray(prev)) return prev;

    return prev.map((user) => userMap[user.id] ?? user);
  });
};
